import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography, Card, IconButton, Badge, Grid, Button, Chip, Tooltip } from "@mui/material";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
// import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BackButton from "../workshop/components/BackButton";
import FilterChip from "../../shared/components/FilterChip";
import ViewTab from "../../shared/components/ViewTab";
import StatCard from "../../shared/components/StatCard";
import { useGetCountOfReservationsByWorkshopIdQuery, useLazyGetReservationsByWorkshopIdQuery } from "../../app/store/api/reservationApi";
import { useParams } from "react-router-dom";
import { STAT_CARDS, STATUS } from "./utils";
import { useTranslation } from "react-i18next";
import WorkshopSettingsPage from "./components/WorkshopSettingsPage";
import { useGetWorkshopByIdQuery } from "../../app/store/api/workshopApi";
import { useDispatch, useSelector } from "react-redux";
import { setWorkshopDetails } from "../../app/store/userSlice";
import { useWindowSize } from "../../shared/hooks/useWindowSize";
import ReservationCard from "../../shared/components/ReservationCard";
import FilterMenu from "../../shared/components/FilterMenu";
import { FILTERS, getStatus, TRANSLATION_MAP } from "../../shared/utils/reservation";
import CardSkeleton from "../../shared/components/CardSkeleton";

const WorkshopOwnerPage = () => {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const [tab, setTab] = useState("bookings");
  const [filter, setFilter] = useState("New");
  // const [showHelpText, setShowHelpText] = useState(false);
  const params = useParams();
  const dispatch = useDispatch();
  const { data: reservationsCount } = useGetCountOfReservationsByWorkshopIdQuery({ workshopId: params.id }, { skip: !params.id });
  const [getPaginatedReservations, { data: reservationsData, isLoading: isLoadingReservations }] = useLazyGetReservationsByWorkshopIdQuery();
  const { data: workshopDetails } = useGetWorkshopByIdQuery(params.id, { skip: !params.id });
  const [expanded, setExpanded] = useState(null);
  const showSkeleton = isLoadingReservations;
  const { reservations } = reservationsData || {};
  const [limit, setLimit] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [tokenData, setTokenData] = useState({ 1: { token: null } });
  const status = STATUS[workshopDetails?.active ? "active" : "inactive"];

  const handleCardClick = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (workshopDetails) {
      dispatch(setWorkshopDetails(workshopDetails));
    }
  }, [dispatch, workshopDetails]);

  useEffect(() => {
    if (!reservations && !showSkeleton && params.id) {
      getPaginatedReservations({ workshopId: params.id, limit, nextToken: null });
    }
  }, [reservations, showSkeleton, getPaginatedReservations, limit]);

  const handleButtonClick = (direction) => {
    window.scrollTo(0, 0);
    if (direction === "next") {
      setCurrentPage((prev) => prev + 1);
      setTokenData((previous) => ({
        ...previous,
        [currentPage + 1]: { token: reservationsData?.pagination?.nextToken },
      }));
      getPaginatedReservations({ workshopId: params.id, limit, nextToken: reservationsData?.pagination?.nextToken });
    } else {
      setCurrentPage((prev) => prev - 1);
      const previousToken = tokenData[currentPage - 1]?.token || null;
      getPaginatedReservations({ workshopId: params.id, limit, nextToken: previousToken });
    }
  };

  const filtered = useMemo(() => {
    if (!reservations) return [];
    if (filter === "All") return reservations;
    return reservations.filter((b) => getStatus(b.status) === filter);
  }, [filter, reservations]);

  const filterChipData = useMemo(() => {
    if (!reservations) return FILTERS.map((f) => ({ ...f, count: 0 }));
    return FILTERS.map((f) => ({
      ...f,
      count: f.key === "All" ? (reservationsCount?.total || 0) : (reservationsCount?.[TRANSLATION_MAP[f.key]] || 0),
    }));
  }, [reservationsCount, reservations]);

  const statCardData = useMemo(() => {
    if (!reservations) return STAT_CARDS.map((c) => ({ ...c, value: 0 }));
    return STAT_CARDS.map((c) => ({
      ...c,
      value: reservationsCount?.[c.key] || 0,
    }));
  }, [reservationsCount, reservations]);

  return (
    <Box sx={{ bgcolor: "#F4F6F9", minHeight: "100vh", width: "100%", pb: 6 }}>
      <Box sx={{ bgcolor: "#003C59", px: { xs: 2, md: 5 }, pt: 3, pb: 3, width: "100%" }}>
        <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
          <Box>
            <BackButton />
            <Stack direction='row' spacing={1.5} alignItems='center'>
              <Box
                sx={{
                  bgcolor: "#00C9DB",
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <BuildOutlinedIcon sx={{ color: "#003C59", fontSize: 20 }} />
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box>
                  <Typography variant='h5' sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}>
                    {workshopDetails?.name || t("userDashboard.bookings")}
                  </Typography>
                  <Typography variant='body2' sx={{ color: "rgba(255,255,255,0.6)" }}>
                    {t("userDashboard.subTitle")}
                  </Typography>
                </Box>
                <Box sx={{ cursor: "default" }}>
                  <Tooltip title={t(`workshopStatus.${status.key === "active" ? "activeText" : "inactiveText"}`)} placement='top' arrow>
                    <Chip
                      size='small'
                      label={t(`adminDashboard.${status.key}`)}
                      sx={{
                        bgcolor: status.bg,
                        color: status.color,
                        fontWeight: 700,
                        fontSize: 12,
                      }}
                    />
                  </Tooltip>
                </Box>
              </Box>
            </Stack>
          </Box>

          {/* <Badge badgeContent={2} color="error">
                <IconButton sx={{ bgcolor: "rgba(255,255,255,0.06)", color: "#fff" }}>
                    <NotificationsNoneOutlinedIcon />
                </IconButton>
            </Badge> */}
        </Stack>

        <Grid container spacing={2} sx={{ mt: 3, display: "flex", flexWrap: "wrap", justifyContent: "space-between", flexGrow: 1 }}>
          {statCardData.map((c) => (
            <StatCard key={c.key} label={t(`userDashboard.${c.key}`)} value={c.value} Icon={c.icon} emphasize={c.emphasize} />
          ))}
        </Grid>
      </Box>

      <Box sx={{ px: { xs: 2, md: 5 }, mt: 3, width: "100%" }}>
        <Stack direction='row' spacing={1} sx={{ mb: 3 }}>
          <ViewTab active={tab === "bookings"} icon={EventNoteOutlinedIcon} label={t("userDashboard.bookings")} count={reservationsCount?.new || 0} onClick={() => setTab("bookings")} />
          <ViewTab active={tab === "edit"} icon={EditOutlinedIcon} label={t("userDashboard.edit")} onClick={() => setTab("edit")} />
        </Stack>

        {tab === "bookings" ? (
          <>
            <Stack direction='row' spacing={1.25} sx={{ mb: 3, flexWrap: "wrap" }}>
              {isMobile ? (
                <FilterMenu options={filterChipData} setFilter={setFilter} filter={filter} />
              ) : (
                filterChipData.map((f) => <FilterChip key={f.key} label={t(`userDashboard.${TRANSLATION_MAP[f.key]}`)} count={f.count} active={filter === f.key} onClick={() => setFilter(f.key)} />)
              )}
            </Stack>

            <Stack spacing={1.5}>
              {showSkeleton ? (
                <CardSkeleton />
              ) : filtered.length === 0 ? (
                <Card elevation={0} sx={{ p: 4, textAlign: "center", border: "1px dashed #E3E7EE" }}>
                  <Typography color='text.secondary'>{t("userDashboard.noBookingText")}</Typography>
                </Card>
              ) : (
                filtered.map((b, index) => <ReservationCard key={b.PK} reservation={b} index={index} expanded={expanded === b.PK} handleCardClick={handleCardClick} adminView={false} />)
              )}
            </Stack>
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
              <Button variant='outlined' onClick={() => handleButtonClick("previous")} disabled={currentPage === 1}>
                {t("userDashboard.previous")}
              </Button>
              <Button variant='outlined' onClick={() => handleButtonClick("next")} disabled={!reservationsData?.pagination?.hasMore}>
                {t("userDashboard.next")}
              </Button>
            </Box>
          </>
        ) : (
          <WorkshopSettingsPage />
        )}
      </Box>

      {/* {showHelpText && (
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                        position: "fixed",
                        right: 24,
                        bottom: 90,
                        bgcolor: "#fff",
                        borderRadius: 2,
                        boxShadow: "0 6px 20px rgba(20,30,50,0.15)",
                        px: 1.5,
                        py: 1,
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t("userDashboard.needHelp")}
                    </Typography>
                </Stack>
            )}
            <IconButton
                sx={{
                    position: "fixed",
                    right: 24,
                    bottom: 24,
                    bgcolor: "#00C9DB",
                    color: "#fff",
                    width: 52,
                    height: 52,
                    "&:hover": { bgcolor: "#C9532F" },
                }}
                onMouseEnter={() => setShowHelpText(true)}
                onMouseLeave={() => setShowHelpText(false)}
            >
                <ChatBubbleOutlineIcon />
            </IconButton> */}
    </Box>
  );
};

export default WorkshopOwnerPage;
